import React from "react";

export const MACHINE_FORM_HELP_TITLE = "Guia do cadastro de máquina";

export function MachineFormHelpContent({ meterType = "hour_meter" }) {
  const usesHour = meterType === "hour_meter" || meterType === "both";
  const usesKm = meterType === "odometer" || meterType === "both";

  return (
    <>
      <p>
        Este formulário define <strong>como a máquina aparece na plataforma</strong>{" "}
        e <strong>quais dados o operador precisa informar no app de campo</strong>.
        Quanto mais informação você pedir, melhor ficam relatórios, filtros e
        alertas — mas cada foto ou campo extra <strong>aumenta o tempo do operador</strong>{" "}
        e pode demorar um pouco mais para os dados aparecerem na web quando há
        envio de imagens.
      </p>

      <p>
        <strong>Identificação</strong> — código ou nome que a equipe usa no dia a
        dia (número de frota, apelido). É o que o operador digita ou lê no QR
        Code ao assumir a máquina.
      </p>

      <p>
        <strong>ID do dispositivo</strong> — código do rastreador GPS instalado
        na máquina. Diferente da identificação de frota: liga telemetria,
        localização e alertas de velocidade ao cadastro correto.
      </p>

      <p>
        <strong>Número do chassi</strong> e <strong>ano de fabricação</strong> —
        opcionais, mas úteis para manutenção, compra de peças e histórico da
        frota.
      </p>

      <p>
        <strong>Tipo de medidor</strong> — define se o operador informa{" "}
        <strong>horímetro (horas)</strong>, <strong>hodômetro (km)</strong> ou{" "}
        <strong>ambos</strong>. Tratores e implementos agrícolas costumam usar
        horas; caminhões e veículos rodoviários, quilometragem.
      </p>

      <p>
        <strong>App de campo — ao assumir a máquina</strong>
      </p>
      <ul>
        <li>
          <strong>Foto do horímetro/hodômetro</strong> — segunda prova da leitura
          informada. Ajuda a conferir fraudes ou erros de digitação. Exigida na{" "}
          <strong>abertura e no fechamento</strong> do trabalho. Sem a foto, o
          operador só digita o valor — mais rápido, com menos evidência.
        </li>
        <li>
          <strong>Informar hectares</strong> — o operador registra o contador de
          hectares do painel. Permite calcular{" "}
          <strong>consumo médio de combustível por hectare</strong>, comparar
          rendimento entre operadores e usar filtros por área trabalhada na
          plataforma. Recomendado em máquinas com contador de hectares (colheitadeiras,
          pulverizadores, plantadeiras).
        </li>
        <li>
          <strong>Foto do contador de hectares</strong> — comprova visualmente o
          valor informado, como a foto do medidor. Útil quando há dúvida sobre
          leituras ou disputa entre operadores.
        </li>
      </ul>

      <p>
        <strong>Velocidade máxima (km/h)</strong> — limite para esta máquina.
        Trabalhos acima desse valor geram alerta automático em{" "}
        <strong>Ações pendentes e alertas</strong>. Opcionalmente, o mesmo alerta
        pode ser enviado por <strong>WhatsApp</strong> ao gestor selecionado
        (pode haver custo por mensagem).
      </p>

      <p>
        <strong>Combustível — alertas de abastecimento</strong> — dados usados
        pela plataforma para detectar erros ou fraudes em cada abastecimento
        registrado no app:
      </p>
      <ul>
        <li>
          <strong>Capacidade do tanque (L)</strong> — alerta se o operador
          informar mais litros do que o tanque comporta, ou marcar tanque cheio
          de forma inconsistente com o histórico.
        </li>
        {usesHour ? (
          <li>
            <strong>Consumo mínimo e máximo (L/h)</strong> — faixa esperada entre
            abastecimentos com base nas horas de horímetro. Valores fora da faixa
            geram alerta de abastecimento inconsistente.
          </li>
        ) : null}
        {usesKm ? (
          <li>
            <strong>Rendimento mínimo e máximo (km/L)</strong> — faixa esperada
            de quilômetros por litro entre abastecimentos. Valores improváveis
            geram alerta.
          </li>
        ) : null}
        <li>
          A plataforma também compara a leitura do medidor com o registro
          anterior (incluindo alerta quando há novo abastecimento sem avanço de
          horímetro ou hodômetro).
        </li>
      </ul>

      <p>
        <strong>App de campo — no abastecimento</strong> — regras quando o
        operador registra combustível:
      </p>
      <ul>
        <li>
          A <strong>leitura manual</strong> do horímetro ou hodômetro é{" "}
          <strong>sempre obrigatória</strong> — não depende de checkbox.
        </li>
        <li>
          <strong>Foto do medidor</strong> — opcional por máquina; comprova a
          leitura no momento do abastecimento.
        </li>
        <li>
          <strong>Foto do marcador de combustível</strong> — registra o nível do
          tanque antes ou depois do abastecimento.
        </li>
        <li>
          <strong>Foto do abastecimento</strong> — bomba, nota fiscal ou
          comprovante; reforça auditoria de litros e custo.
        </li>
        <li>
          <strong>Informar hectares</strong> — hectares no momento do
          abastecimento; alimenta análises de consumo por área e filtros
          cruzados na plataforma.
        </li>
        <li>
          <strong>Foto do contador de hectares</strong> — evidência visual
          independente de exigir o valor numérico.
        </li>
      </ul>

      <p>
        <strong>Observações da máquina</strong> — texto livre para a equipe
        (modelo, motor, placa, oficina, peculiaridades). Não afeta alertas, mas
        ajuda quem edita ou mantém o cadastro depois.
      </p>

      <p>
        <strong>Como escolher o que exigir?</strong>
      </p>
      <ul>
        <li>
          <strong>Mínimo de fricção</strong> — só leitura manual no
          abastecimento; sem fotos ao assumir. Bom para frotas grandes com
          operadores experientes.
        </li>
        <li>
          <strong>Equilíbrio</strong> — foto do medidor ao assumir e capacidade
          do tanque preenchida; hectares só onde a máquina tem contador.
        </li>
        <li>
          <strong>Máxima rastreabilidade</strong> — fotos em abertura, fechamento
          e abastecimento; hectares informados e fotografados. Melhor para
          auditoria, disputas e relatórios detalhados — exige mais tempo do
          operador e upload de imagens.
        </li>
      </ul>

      <p>
        Campos de combustível em branco <strong>desativam</strong> aquela regra de
        alerta. Checkboxes desmarcadas <strong>não exigem</strong> foto ou
        hectares no app — o operador informa só o que for obrigatório por padrão
        (medidor no abastecimento).
      </p>
    </>
  );
}
